import React from "react";
import { tenantHref } from "@/lib/tenantPath";

/**
 * BlogGrid — shared journal/blog card grid for the five themed templates (t2-t6).
 * Classic keeps its own layout (components/site/Blog.jsx): its markup is materially
 * different, an image-led asymmetric design rather than a uniform grid.
 *
 * Before this component existed, every one of the five themes repeated the same two
 * defects independently:
 *   - BUG-012: a post with a real `slug` still rendered as a bare, unclickable
 *     `<article>` — nothing on the card led to the post.
 *   - BUG-029: the grid's column count was hardcoded to 3, so a practice with only 1 or
 *     2 posts (real or the static sample) rendered 1-2 empty, visibly bordered/tinted
 *     cells next to the real ones.
 *
 * Each theme still owns its card's whole visual language (colors, radii, borders, hover
 * states) via `cardClassName`/`renderCard`; this component owns only the grid sizing and
 * the click target.
 *
 * The click target is a plain `<a href>` (via `tenantHref`, like every other internal
 * link these five templates render — /reserver, /boutique, mailto:…), not react-router's
 * `<Link>`: none of these templates import react-router-dom today, Classic's Blog.jsx is
 * the only file in the tree that does, and it is not covered by any existing test. Every
 * themed-template test in this repo (themeChrome.test.js, themeContactAndFooter.test.js)
 * mounts these files WITHOUT mocking react-router-dom, which cannot even be `require`d
 * under this project's Jest config (its package.json "main" points at a file that does
 * not exist — confirmed empirically, not a guess). Introducing it here would silently
 * break every one of those suites.
 */

// Every count actually used below must be a LITERAL string somewhere in this file:
// Tailwind's JIT scans source TEXT for class names, not values assembled at runtime, so a
// class built by string concatenation here would be invisible to it and render with no
// columns at all in a production build.
const COL_CLASSES = {
  1: "grid-cols-1",
  2: "grid-cols-1 md:grid-cols-2",
  3: "grid-cols-1 md:grid-cols-3",
};

/** Column classes for a grid sized to how many posts there actually are. useBlogPosts
 *  never returns more than 3 (real or sample), so anything above 3 clamps to the same
 *  3-column layout as before. */
export function blogGridColsClass(count) {
  const n = Math.min(Math.max(Number(count) || 1, 1), 3);
  return COL_CLASSES[n];
}

export default function BlogGrid({ posts, gridClassName = "", gridStyle, cardClassName = "", cardStyle, renderCard }) {
  return (
    <div data-testid="blog-grid" className={`grid ${blogGridColsClass(posts.length)} ${gridClassName}`} style={gridStyle}>
      {posts.map((post, i) => {
        const testId = `blog-card-${i}`;
        const content = renderCard(post, i);
        if (post.slug) {
          return (
            <a key={i} href={tenantHref(`/journal/${post.slug}`)} data-testid={testId} className={`block ${cardClassName}`} style={cardStyle}>
              {content}
            </a>
          );
        }
        return (
          <div key={i} data-testid={testId} className={`block ${cardClassName}`} style={cardStyle}>
            {content}
          </div>
        );
      })}
    </div>
  );
}
